import { Text, ThemeTypings, Tooltip } from '@chakra-ui/react';
import { useMemo } from 'react';

type TextAlign = 'left' | 'center' | 'right';
export interface TextCellProps {
    value?: string | number | React.ReactElement;
    disabled?: boolean;
    placeholder?: string | null;
    textAlign?: TextAlign;
    highlight?: string[];
    noMaxWidth?: boolean;
    size?: ThemeTypings['fontSizes'];
}

export const TextCell = ({
    value,
    disabled,
    placeholder = '',
    textAlign = 'left',
    highlight,
    noMaxWidth,
    size,
}: TextCellProps) => {
    const highlightTerms = useMemo(() => {
        if (!highlight || !highlight.length) {
            return;
        }

        return highlight.map((h) => h.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&').toLowerCase());
    }, [highlight]);

    const content = useMemo(() => {
        if (!value) {
            return placeholder;
        }

        if (typeof value !== 'string' || !highlightTerms) {
            return value;
        }

        return value.split(new RegExp(`(${highlightTerms.join('|')})`, 'gi'));
    }, [highlightTerms, placeholder, value]);

    return (
        <Tooltip label={value} isDisabled={typeof value === 'string' && value?.length < 55} placement="bottom-start">
            <Text
                textAlign={textAlign}
                color={disabled ? 'whiteAlpha.400' : ''}
                textOverflow="ellipsis"
                overflow="hidden"
                maxWidth={noMaxWidth ? undefined : '450px'}
                fontSize={size}
                sx={{
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {Array.isArray(content)
                    ? content.map((part: string, idx: number) => {
                          const isMatch = highlightTerms?.includes(part.toLowerCase());
                          return (
                              <Text
                                  key={idx}
                                  background={isMatch ? 'teal.300' : 'inherit'}
                                  color={isMatch ? 'gray.800' : 'inherit'}
                                  fontWeight={isMatch ? 'medium' : 'inherit'}
                                  as="span"
                                  fontSize={size}
                              >
                                  {part}
                              </Text>
                          );
                      })
                    : content}
            </Text>
        </Tooltip>
    );
};
